import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { Search as SearchIcon, ViewList as ListIcon, CalendarMonth as CalendarIcon } from '@mui/icons-material';
import { DEFAULT_FILTERS } from './orderFilters';

/**
 * What the provider can filter on. The backend keeps legacy aliases (DISCUSSIONED, PAID_AND_READY,
 * FINISHED; CREATED/ACTIVE are the same inquiry stage), which read as duplicates in a dropdown, so
 * each option is a GROUP of statuses with one label; `filters.statuses` still holds backend codes.
 */
export const STATUS_GROUPS = [
  { key: 'new', statuses: ['CREATED', 'ACTIVE'], labelKey: 'orders.filters.statusNew' },
  { key: 'discussion', statuses: ['DISCUSSION', 'DISCUSSIONED'], labelKey: 'orders.statusDISCUSSION' },
  { key: 'confirmed', statuses: ['CONFIRMED'], labelKey: 'orders.statusCONFIRMED' },
  { key: 'paid', statuses: ['PAID', 'PAID_AND_READY'], labelKey: 'orders.statusPAID' },
  { key: 'completed', statuses: ['COMPLETED', 'FINISHED'], labelKey: 'orders.statusCOMPLETED' },
  { key: 'cancelledByCustomer', statuses: ['CANCELLED_BY_USER'], labelKey: 'orders.statusCANCELLED_BY_USER' },
  { key: 'cancelledByProvider', statuses: ['CANCELLED_BY_PROVIDER'], labelKey: 'orders.statusCANCELLED_BY_PROVIDER' },
  { key: 'paymentFailed', statuses: ['CANCELLED_PAYMENT_FAILED'], labelKey: 'orders.statusCANCELLED_PAYMENT_FAILED' },
];

const selectedGroupKeys = (statuses) =>
  STATUS_GROUPS.filter((g) => g.statuses.every((s) => statuses.includes(s))).map((g) => g.key);

const statusesForGroups = (keys) =>
  STATUS_GROUPS.filter((g) => keys.includes(g.key)).flatMap((g) => g.statuses);

const isDirty = (filters) => Object.keys(DEFAULT_FILTERS).some((k) => JSON.stringify(filters[k]) !== JSON.stringify(DEFAULT_FILTERS[k]));

/**
 * Props: filters, onFiltersChange(next), view ('list' | 'calendar'), onViewChange(view),
 * calendarAvailable (boolean), calendarLocked (boolean: show the toggle disabled with a Pro hint),
 * onUpgradeClick.
 */
export default function OrdersToolbar({
  filters, onFiltersChange, view, onViewChange, calendarAvailable, calendarLocked = false, onUpgradeClick,
}) {
  const { t } = useTranslation();
  const set = (patch) => onFiltersChange({ ...filters, ...patch });

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }} data-testid="orders-toolbar">
      {calendarAvailable && (
        <Tooltip title={calendarLocked ? t('orders.view.calendarProOnly') : ''}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(e, v) => {
              if (!v) return;
              if (v === 'calendar' && calendarLocked) { onUpgradeClick?.(); return; }
              onViewChange(v);
            }}
          >
            <ToggleButton value="list" aria-label={t('orders.view.list')}>
              <ListIcon fontSize="small" sx={{ mr: 0.5 }} />{t('orders.view.list')}
            </ToggleButton>
            <ToggleButton value="calendar" aria-label={t('orders.view.calendar')}>
              <CalendarIcon fontSize="small" sx={{ mr: 0.5 }} />{t('orders.view.calendar')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>
      )}

      <ToggleButtonGroup size="small" exclusive value={filters.scope} onChange={(e, v) => v && set({ scope: v })}>
        <ToggleButton value="open">{t('orders.filters.scopeOpen')}</ToggleButton>
        <ToggleButton value="all">{t('orders.filters.scopeAll')}</ToggleButton>
      </ToggleButtonGroup>

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel id="orders-status-filter">{t('orders.filters.status')}</InputLabel>
        <Select
          labelId="orders-status-filter"
          multiple
          value={selectedGroupKeys(filters.statuses)}
          onChange={(e) => set({ statuses: statusesForGroups(e.target.value) })}
          input={<OutlinedInput label={t('orders.filters.status')} />}
          renderValue={(keys) => STATUS_GROUPS.filter((g) => keys.includes(g.key)).map((g) => t(g.labelKey)).join(', ')}
          data-testid="orders-status-filter"
        >
          {STATUS_GROUPS.map((g) => (
            <MenuItem key={g.key} value={g.key}>
              <Checkbox size="small" checked={g.statuses.every((s) => filters.statuses.includes(s))} />
              <ListItemText primary={t(g.labelKey)} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {view === 'list' && (
        <ToggleButtonGroup size="small" exclusive value={filters.time} onChange={(e, v) => v && set({ time: v })}>
          <ToggleButton value="all">{t('orders.filters.timeAll')}</ToggleButton>
          <ToggleButton value="upcoming">{t('orders.filters.timeUpcoming')}</ToggleButton>
          <ToggleButton value="past">{t('orders.filters.timePast')}</ToggleButton>
          <ToggleButton value="undated">{t('orders.filters.timeUndated')}</ToggleButton>
        </ToggleButtonGroup>
      )}

      <TextField
        size="small" type="date" label={t('orders.filters.from')} value={filters.from}
        onChange={(e) => set({ from: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
      />
      <TextField
        size="small" type="date" label={t('orders.filters.to')} value={filters.to}
        onChange={(e) => set({ to: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
      />

      <TextField
        size="small" placeholder={t('orders.filters.search')} value={filters.query}
        onChange={(e) => set({ query: e.target.value })} sx={{ flex: '1 1 240px' }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
      />

      {isDirty(filters) && (
        <Button size="small" onClick={() => onFiltersChange({ ...DEFAULT_FILTERS })}>{t('orders.filters.clear')}</Button>
      )}
    </Box>
  );
}
